import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import { fmtRelative } from "../../lib/utils-app";
import { Sparkles, RefreshCw, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Feed() {
  const [items, setItems] = useState([]);
  const [minScore, setMinScore] = useState(0);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [generating, setGenerating] = useState(null);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get(`/raw-items?min_score=${minScore}&limit=200`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { document.title = "Ingested Feed — IndiaODR Admin"; load(); /* eslint-disable-next-line */ }, [minScore]);

  const runIngest = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/ingest/run");
      toast.success(`Ingestion complete: ${data.stored} new, ${data.duplicates} duplicates across ${data.sources} sources.`);
      load();
    } catch { toast.error("Ingestion failed. Check sources."); }
    finally { setRunning(false); }
  };

  const generateDraft = async (itemId) => {
    setGenerating(itemId);
    try {
      // Kick off background job
      const { data: job } = await api.post("/drafts/generate", { raw_item_ids: [itemId] });
      toast.info("AI drafting started — Claude is writing. This typically takes 30–90 seconds.");

      // Poll until done / error / 5-minute timeout
      const start = Date.now();
      const poll = async () => {
        if (Date.now() - start > 5 * 60 * 1000) {
          throw new Error("Drafting timed out after 5 minutes.");
        }
        const { data } = await api.get(`/drafts/generate/jobs/${job.job_id}`);
        if (data.status === "done" && data.draft) {
          toast.success(`Draft generated: ${data.draft.headline}`);
          nav(`/admin/drafts/${data.draft.id}`);
          return;
        }
        if (data.status === "error") {
          if (data.error_code === "llm_budget") {
            throw new Error("LLM quota exceeded. Top up the Universal Key balance and retry.");
          }
          throw new Error(data.error || "AI drafting failed.");
        }
        await new Promise((r) => setTimeout(r, 2500));
        return poll();
      };
      await poll();
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Draft generation failed.");
    } finally {
      setGenerating(null);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow" style={{ color: "#D4AF37" }}>Ingestion</div>
          <h1 className="font-serif text-[32px] md:text-[40px] mt-1 text-[var(--io-text)]">Raw intelligence feed</h1>
        </div>
        <button onClick={runIngest} disabled={running} className="btn btn-primary" data-testid="run-ingest-button">
          <RefreshCw size={14} className={running ? "animate-spin" : ""}/> {running ? "Fetching…" : "Fetch all sources"}
        </button>
      </div>

      <div className="io-card-el p-4 mb-5 flex items-center gap-4 flex-wrap" data-testid="feed-filters">
        <span className="eyebrow">Min relevance</span>
        <input type="range" min="0" max="100" value={minScore} onChange={(e) => setMinScore(+e.target.value)} data-testid="min-score-slider" className="accent-[var(--io-gold)]"/>
        <span className="font-mono text-[13px] text-[var(--io-gold)]">{minScore}</span>
        <span className="ml-auto font-mono text-[11px] text-[var(--io-muted)]">{items.length} items · scored {items.filter(i => i.relevance_score >= 70).length} high-value</span>
      </div>

      <div className="io-card-el divide-y divide-[var(--io-border)]" data-testid="feed-list">
        {loading && <div className="p-10 text-[var(--io-muted)]">Loading feed…</div>}
        {!loading && items.length === 0 && <div className="p-10 text-[var(--io-muted)]" data-testid="feed-empty">No items match. Lower the threshold or run fetch.</div>}
        {items.map((i) => (
          <div key={i.id} className="p-5 flex items-start justify-between gap-6" data-testid={`raw-${i.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[11px] font-mono">
                <span className="text-[var(--io-gold)]">{i.source_name}</span>
                <span className="text-[var(--io-muted)]">·</span>
                <span className="text-[var(--io-text-2)]">{fmtRelative(i.fetched_at)}</span>
                <span className="text-[var(--io-muted)]">·</span>
                <span className={`${i.relevance_score >= 70 ? "text-[#10B981]" : i.relevance_score >= 40 ? "text-[#F59E0B]" : "text-[var(--io-muted)]"}`}>Score {Math.round(i.relevance_score)}</span>
              </div>
              <a href={i.url} target="_blank" rel="noreferrer" className="block font-serif text-[18px] mt-1.5 text-[var(--io-text)] hover:text-[var(--io-gold)] leading-snug">{i.title}</a>
              {i.snippet && <p className="mt-2 text-[13px] text-[var(--io-text-2)] line-clamp-2 max-w-[80ch]">{i.snippet}</p>}
              {i.score_breakdown?.sensitive_flags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {i.score_breakdown.sensitive_flags.map((f) => (
                    <span key={f} className="io-badge io-badge-amber"><AlertTriangle size={10}/> {f.replace(/_/g, " ")}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="shrink-0">
              <button onClick={() => generateDraft(i.id)} disabled={generating === i.id} className="btn btn-primary !text-[12px] !py-2 !px-3" data-testid={`generate-${i.id}`}>
                <Sparkles size={14}/> {generating === i.id ? "Drafting…" : "AI draft"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
